import type { HomePageData } from '../../../shared/models/marketing.models';
import { successStorySlug } from '../../utils/milestone-cta';
import type { StoryCardModel, StoryCategoryId } from '../success-stories/success-stories.utils';
import { sortByCreatedAtDesc } from '../success-stories/success-stories.utils';

export type TestimonialItem = HomePageData['testimonials']['items'][number];

/** Defaults for CMS section heading + public page fallbacks. */
export const TESTIMONIALS_SECTION_DEFAULTS = {
  badge: 'Student Voices',
  title: 'Student Testimonials',
  subtitle:
    'Hear from students who achieved their study abroad goals with Career Grad Global.',
} as const;

/** @deprecated Prefer TESTIMONIALS_SECTION_DEFAULTS — kept for older imports. */
export const TESTIMONIALS_PAGE_HERO = TESTIMONIALS_SECTION_DEFAULTS;

export const TESTIMONIALS_ALL_PAGE_HERO = {
  badge: TESTIMONIALS_SECTION_DEFAULTS.badge,
  title: 'All Testimonials',
  subtitle:
    'Browse every published review from students who achieved their global education goals with Career Grad Global.',
} as const;

const CATEGORY_KEYWORDS: Record<Exclude<StoryCategoryId, 'all'>, string[]> = {
  scholarships: ['scholarship', 'award', 'grant', 'funding', 'excellence'],
  masters: ['master', 'msc', 'mba', 'm.sc', "master's", 'postgraduate'],
  phd: ['phd', 'research', 'doctorate', 'doctoral', 'phd track'],
  journeys: ['journey', 'student', 'experience', 'placement'],
};

const PILL_BY_CATEGORY: Partial<Record<Exclude<StoryCategoryId, 'all'>, string>> = {
  scholarships: 'Scholarship Success',
  masters: 'Student Journeys',
  phd: 'PhD Track',
  journeys: 'Student Journeys',
};

function inferCategoryId(item: TestimonialItem): Exclude<StoryCategoryId, 'all'> {
  const haystack = [item.course, item.review, item.university].filter(Boolean).join(' ').toLowerCase();
  if (CATEGORY_KEYWORDS.phd.some((kw) => haystack.includes(kw))) return 'phd';
  if (CATEGORY_KEYWORDS.scholarships.some((kw) => haystack.includes(kw))) return 'scholarships';
  if (CATEGORY_KEYWORDS.masters.some((kw) => haystack.includes(kw))) return 'masters';
  return 'journeys';
}

function testimonialMetaLine(item: TestimonialItem): string | undefined {
  const parts: string[] = [];
  const university = item.university?.trim();
  const country = item.country?.trim();
  if (university && country) parts.push(`${university}, ${country}`);
  else if (university) parts.push(university);
  else if (country) parts.push(country);
  const course = item.course?.trim();
  if (course) parts.push(course);
  return parts.length ? parts.join(' · ') : undefined;
}

export function testimonialToStoryCard(item: TestimonialItem): StoryCardModel {
  const savedPill = item.pill?.trim();
  const categoryId = inferCategoryId(item);
  const category = savedPill || PILL_BY_CATEGORY[categoryId] || 'Student Journeys';

  return {
    id: `testimonial-${successStorySlug(item.studentName + (item.university ?? ''))}`,
    pill: category,
    name: item.studentName,
    meta: testimonialMetaLine(item),
    description: item.review,
    image: item.avatar,
    kind: 'testimonial',
    category,
    university: item.university?.trim() || undefined,
    country: item.country?.trim() || undefined,
    course: item.course?.trim() || undefined,
    rating: item.rating,
  };
}

export function mapTestimonialCards(items: TestimonialItem[]): StoryCardModel[] {
  return sortByCreatedAtDesc(items).map(testimonialToStoryCard);
}
